/**
 * FASE 22.2 — WORKFLOW SELECTORS
 *
 * Pure read functions over workflow state.
 * No mutations, no side effects.
 */

import {
  getWorkflows,
  getActiveWorkflows,
  getWorkflowById,
} from "./workflowState";
import { WORKFLOW_STATUS, STEP_STATUS } from "./workflowSchemas";

export function getWorkflowsByType(type) {
  return getWorkflows().filter((w) => w.type === type);
}

export function getWorkflowsByStatus(status) {
  return getWorkflows().filter((w) => w.status === status);
}

export function getWorkflowsByPriority(priority) {
  return getActiveWorkflows().filter((w) => w.priority === priority);
}

export function getWorkflowsByEntity(entityId) {
  if (!entityId) return [];
  return getWorkflows().filter((w) =>
    w.related_entities.some((e) => e.id === entityId),
  );
}

export function getActiveWorkflowCount() {
  return getActiveWorkflows().length;
}

export function getWorkflowsByTypeAndStatus(type, status) {
  return getWorkflows().filter((w) => w.type === type && w.status === status);
}

export function hasWorkflowForEntity(entityId, type = null) {
  const matches = getWorkflowsByEntity(entityId).filter(
    (w) => w.status === WORKFLOW_STATUS.ACTIVE || w.status === WORKFLOW_STATUS.PAUSED,
  );
  if (!type) return matches.length > 0;
  return matches.some((w) => w.type === type);
}

/** Returns all steps for a workflow. */
export function getStepsForWorkflow(workflowId) {
  return getWorkflowById(workflowId)?.steps ?? [];
}

/** Returns steps in PENDING or IN_PROGRESS state. */
export function getPendingStepsForWorkflow(workflowId) {
  return getStepsForWorkflow(workflowId).filter(
    (s) => s.status === STEP_STATUS.PENDING || s.status === STEP_STATUS.IN_PROGRESS,
  );
}

/** Returns the first PENDING step (the natural next step). */
export function getNextStepForWorkflow(workflowId) {
  return getStepsForWorkflow(workflowId).find((s) => s.status === STEP_STATUS.PENDING) ?? null;
}

/** Returns active workflows that haven't had activity in more than `thresholdDays`. */
export function getWorkflowsNeedingAttention(thresholdDays = 3) {
  const cutoff = Date.now() - thresholdDays * 24 * 60 * 60 * 1000;
  return getActiveWorkflows().filter(
    (w) => w.status === WORKFLOW_STATUS.ACTIVE && w.last_activity < cutoff,
  );
}

/** Returns the entity name label for display (first related_entity.name). */
export function getWorkflowEntityLabel(workflowId) {
  const wf = getWorkflowById(workflowId);
  return wf?.related_entities?.[0]?.name ?? null;
}

/** Returns workflows sorted by last_activity descending (most recent first). */
export function getWorkflowsSortedByActivity() {
  return getActiveWorkflows().slice().sort((a, b) => b.last_activity - a.last_activity);
}
